<template>
  <div class="appointment-report nap-combo">
    <span
      class="nap-control__lead nap-combo__lead"
      aria-hidden="true"
    >
      <span
        v-if="picked"
        :key="picked.id"
        class="ar-avatar ar-avatar--sm ar-anim-chip-pop"
      >{{ picked.initials }}</span>
      <feather-icon
        v-else
        :icon="icon"
        size="16"
      />
    </span>
    <input
      :id="inputId"
      ref="input"
      class="nap-field nap-field--lead"
      :class="{ 'nap-field--trail': chevron || loading }"
      type="text"
      role="combobox"
      autocomplete="off"
      aria-autocomplete="list"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-controls="listboxId"
      :aria-activedescendant="activeDescendant"
      :aria-required="required ? 'true' : null"
      :aria-invalid="state === 'danger' || state === 'warning' ? 'true' : 'false'"
      :aria-describedby="describedBy || null"
      :aria-busy="loading ? 'true' : null"
      :placeholder="placeholder"
      :disabled="disabled"
      :value="query"
      :data-state="state || null"
      @input="onInput"
      @focus="onFocus"
      @click="openList"
      @blur="onBlur"
      @keydown="onKeydown"
    >
    <span
      v-if="loading"
      class="nap-control__trail"
      aria-hidden="true"
    >
      <svg
        class="ar-spinner"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle
          cx="12"
          cy="12"
          r="9"
          stroke="currentColor"
          stroke-opacity=".25"
          stroke-width="2.5"
        />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
        />
      </svg>
    </span>
    <span
      v-else-if="chevron"
      class="nap-control__trail nap-combo__chevron"
      :class="{ 'nap-combo__chevron--open': isOpen }"
      aria-hidden="true"
    >
      <feather-icon
        icon="ChevronDownIcon"
        size="16"
      />
    </span>

    <transition name="ar-pop">
      <div
        v-if="isOpen"
        :id="listboxId"
        ref="listbox"
        role="listbox"
        :aria-labelledby="labelId || null"
        class="nap-combo__listbox"
        @mousedown.prevent
      >
        <div
          v-for="(option, index) in visibleOptions"
          :id="optionId(index)"
          :key="option.id"
          role="option"
          :aria-selected="isPicked(option) ? 'true' : 'false'"
          class="nap-combo__option"
          :class="{ 'nap-combo__option--active': index === activeIndex }"
          @mouseenter="activeIndex = index"
          @click="pick(option)"
        >
          <span
            class="ar-avatar nap-combo__option-avatar"
            aria-hidden="true"
          >{{ option.initials }}</span>
          <span class="nap-combo__option-text">
            <span class="nap-combo__option-name">
              {{ option.name }}<span
                v-if="option.suffix"
                class="nap-combo__option-suffix"
              > {{ option.suffix }}</span>
            </span>
            <span
              v-if="option.caption"
              class="nap-combo__option-caption"
            >{{ option.caption }}</span>
          </span>
          <feather-icon
            v-if="isPicked(option)"
            icon="CheckIcon"
            size="16"
            class="nap-combo__option-check"
            aria-hidden="true"
          />
        </div>
        <div
          v-if="showEmpty"
          role="option"
          aria-disabled="true"
          aria-selected="false"
          class="nap-combo__empty"
        >
          {{ emptyText }}
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { nextReportUid } from '../../helpers/report_ui'

// Mesmo gatilho da busca de clientes da sidebar (`DynamicSelect`): mais de 2 letras, 500ms depois da última tecla.
const REMOTE_MIN_CHARS = 3
const REMOTE_DEBOUNCE_MS = 500
// Tempo para o clique numa opção chegar antes de o blur fechar a lista.
const BLUR_CLOSE_MS = 200

const normalize = text => String(text || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .trim()

/**
 * Combobox do Novo compromisso (board v4-novo-compromisso): input com a inicial do escolhido à esquerda e uma
 * lista (listbox e2) com avatar de iniciais, nome, sufixo opcional ("(você)") e check no escolhido.
 * Teclado: ↓/↑ navegam, Enter escolhe, Esc fecha, Tab fecha. Sem escolha, o texto digitado some no blur
 * (uma única opção na lista é escolhida sozinha).
 *
 * - `remote`: a lista vem de fora (`options` já é o resultado da busca); o componente emite `search` com o texto
 *   (mais de 2 letras, 500ms) e digitar desfaz a escolha — é a busca de clientes da sidebar.
 * - local (padrão): filtra `options` pelo texto, sem acento/maiúsculas; o nome exato digitado já escolhe.
 *
 * `options`: `[{ id, name, initials, suffix?, caption? }]`. `v-model` = id escolhido (ou `null`).
 * `state`: '' | 'warning' (falta) | 'danger' (regra) — borda e `aria-invalid`.
 */
export default {
  name: 'NewAppointmentCombobox',
  props: {
    value: {
      type: [Number, String],
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    inputId: {
      type: String,
      required: true,
    },
    labelId: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    emptyText: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: 'SearchIcon',
    },
    remote: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    chevron: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    state: {
      type: String,
      default: '',
    },
    describedBy: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      uid: nextReportUid('nap-combo'),
      query: '',
      picked: null,
      listOpen: false,
      activeIndex: 0,
      searchedQuery: '',
    }
  },
  computed: {
    listboxId() {
      return `${this.uid}-listbox`
    },
    trimmedQuery() {
      return this.query.trim()
    },
    // Com a escolha feita (texto = nome escolhido), a lista mostra tudo; senão filtra pelo texto (modo local).
    visibleOptions() {
      if (this.remote) return this.options
      const text = normalize(this.query)
      if (!text || (this.picked && this.query === this.picked.name)) return this.options

      return this.options.filter(option => normalize(option.name).indexOf(text) !== -1)
    },
    isOpen() {
      if (!this.listOpen || this.disabled) return false
      if (this.remote) return this.trimmedQuery.length >= REMOTE_MIN_CHARS

      return true
    },
    showEmpty() {
      return this.isOpen && !this.loading && this.visibleOptions.length === 0
        && (!this.remote || this.searchedQuery === this.trimmedQuery)
    },
    activeDescendant() {
      if (!this.isOpen || !this.visibleOptions.length) return null
      return this.optionId(Math.min(this.activeIndex, this.visibleOptions.length - 1))
    },
  },
  watch: {
    // Mudança de fora (ex.: o tipo de compromisso mudou e o especialista foi limpo): acompanha o texto.
    value(newValue) {
      const pickedId = this.picked ? this.picked.id : null
      if (newValue === pickedId || (this.isEmpty(newValue) && pickedId === null)) return
      this.syncFromValue(newValue)
    },
    options() {
      if (!this.picked && !this.isEmpty(this.value)) this.syncFromValue(this.value)
      if (this.activeIndex >= this.visibleOptions.length) this.activeIndex = 0
    },
    activeIndex() {
      this.$nextTick(this.scrollActiveIntoView)
    },
  },
  created() {
    if (!this.isEmpty(this.value)) this.syncFromValue(this.value)
  },
  beforeDestroy() {
    clearTimeout(this.$_searchTimer)
    clearTimeout(this.$_blurTimer)
  },
  methods: {
    isEmpty(value) {
      return value === null || value === undefined || value === ''
    },
    optionId(index) {
      return `${this.uid}-option-${index}`
    },
    isPicked(option) {
      return !!this.picked && this.picked.id === option.id
    },
    syncFromValue(value) {
      if (this.isEmpty(value)) {
        this.picked = null
        this.query = ''
        return
      }
      const option = this.options.find(item => item.id === value)
      if (option) {
        this.picked = option
        this.query = option.name
      }
    },
    emitValue(value) {
      this.$emit('input', value)
    },
    // Foco por script (ex.: "Faltam N campos" leva ao campo): não abre a lista.
    focus(options) {
      this.$_suppressOpen = true
      if (this.$refs.input) this.$refs.input.focus(options)
      this.$_suppressOpen = false
    },
    onFocus() {
      clearTimeout(this.$_blurTimer)
      if (!this.$_suppressOpen) this.openList()
    },
    openList() {
      if (this.disabled || this.listOpen) return
      const index = this.picked ? this.visibleOptions.findIndex(option => option.id === this.picked.id) : 0
      this.activeIndex = Math.max(0, index)
      this.listOpen = true
    },
    closeList() {
      this.listOpen = false
    },
    onInput(event) {
      this.query = event.target.value
      this.activeIndex = 0
      this.listOpen = true
      if (this.remote) {
        if (this.picked) {
          this.picked = null
          this.emitValue(null)
        }
        this.scheduleSearch()
        return
      }
      const typed = normalize(this.query)
      const exact = typed ? this.options.find(option => normalize(option.name) === typed) || null : null
      const exactId = exact ? exact.id : null
      const pickedId = this.picked ? this.picked.id : null
      if (exactId !== pickedId) {
        this.picked = exact
        this.emitValue(exactId)
      }
    },
    scheduleSearch() {
      clearTimeout(this.$_searchTimer)
      const keyword = this.trimmedQuery
      if (keyword.length < REMOTE_MIN_CHARS) return
      this.$_searchTimer = setTimeout(() => {
        this.searchedQuery = keyword
        this.$emit('search', keyword)
      }, REMOTE_DEBOUNCE_MS)
    },
    pick(option) {
      if (!option) return
      clearTimeout(this.$_searchTimer)
      this.picked = option
      this.query = option.name
      this.closeList()
      this.emitValue(option.id)
      this.$emit('pick', option)
    },
    onKeydown(event) {
      if (event.ctrlKey || event.metaKey || event.altKey) return
      const count = this.visibleOptions.length
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          if (!this.isOpen) this.openList()
          else if (count) this.activeIndex = (Math.min(this.activeIndex, count - 1) + 1) % count
          break
        case 'ArrowUp':
          event.preventDefault()
          if (this.isOpen && count) this.activeIndex = (Math.min(this.activeIndex, count - 1) - 1 + count) % count
          break
        case 'Enter':
          if (this.isOpen && count) {
            event.preventDefault()
            this.pick(this.visibleOptions[Math.min(this.activeIndex, count - 1)])
          }
          break
        case 'Escape':
          if (this.isOpen) {
            event.preventDefault()
            event.stopPropagation()
            this.closeList()
          }
          break
        case 'Tab':
          this.closeList()
          break
        default:
          break
      }
    },
    onBlur() {
      clearTimeout(this.$_blurTimer)
      this.$_blurTimer = setTimeout(() => {
        if (this.$refs.input && document.activeElement === this.$refs.input) return
        this.closeList()
        this.settleText()
        this.$emit('blur')
      }, BLUR_CLOSE_MS)
    },
    // Ao sair do campo: texto sem escolha some (uma única opção da busca atual é escolhida); com escolha, o texto
    // volta a ser o nome escolhido.
    settleText() {
      if (this.picked) {
        if (this.query !== this.picked.name) this.query = this.picked.name
        return
      }
      if (!this.query) return
      const resultsAreCurrent = !this.remote || (!this.loading && this.searchedQuery === this.trimmedQuery)
      if (resultsAreCurrent && this.visibleOptions.length === 1) {
        this.pick(this.visibleOptions[0])
        return
      }
      clearTimeout(this.$_searchTimer)
      this.query = ''
    },
    scrollActiveIntoView() {
      const list = this.$refs.listbox
      if (!list) return
      const active = list.querySelector('.nap-combo__option--active')
      if (active && typeof active.scrollIntoView === 'function') active.scrollIntoView({ block: 'nearest' })
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/_appointment-report.scss"></style>
<style lang="scss" src="./_new-appointment.scss"></style>

<style lang="scss" scoped>
.nap-combo {
  position: relative;
}

.nap-combo__lead {
  top: 8px;
  left: 8px;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
}

.nap-combo__chevron {
  transition: transform var(--ar-dur-hover) var(--ar-ease-standard);
}

.nap-combo__chevron--open {
  transform: rotate(180deg);
}

// ---- Lista (e2): 46px abaixo do topo do input, rola a partir de ~6 opções ----
.nap-combo__listbox {
  position: absolute;
  top: 46px;
  left: 0;
  right: 0;
  z-index: 40;
  max-height: 296px;
  overflow-y: auto;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  border: 1px solid var(--ar-divider);
  border-radius: var(--ar-radius-card);
  background: var(--ar-bg-raised);
  box-shadow: var(--ar-shadow-e2);
  transform-origin: top center;
}

.nap-combo__option {
  min-height: 44px;
  padding: 6px 10px;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 10px;
  border-radius: var(--ar-radius-control);
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-1);
  cursor: pointer;
  transition: background-color var(--ar-dur-hover) var(--ar-ease-standard);
}

.nap-combo__option--active {
  background: var(--ar-bg-raised-hover);
}

.nap-combo__option-avatar {
  background: var(--ar-bg-field);
}

.nap-combo__option-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.nap-combo__option-name {
  overflow-wrap: anywhere;
}

.nap-combo__option-suffix {
  font-weight: 400;
  color: var(--ar-text-muted);
}

.nap-combo__option-caption {
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ar-text-muted);
}

.nap-combo__option-check {
  flex-shrink: 0;
  color: var(--ar-brand-text);
}

.nap-combo__empty {
  padding: 10px;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-muted);
}
</style>
